import React, { useState, useCallback } from 'react';
import OriginTracer from '@atlassiansox/origin-tracing';
import Button, { LinkButton } from '@atlaskit/button/new';
import { Box, xcss } from '@atlaskit/primitives';
import { Modal, Carousel, CarouselSlide } from '@atlassian/growth-pattern-library-modal';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	MODAL,
	ContextualAnalyticsData,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fireButtonAnalytics } from '../../../common/utils/analytics.tsx';
import { EXPERIMENT_NAME_AB } from '../../../common/utils/constants.tsx';
import { useExperimentEligibility } from '../../../services/check-experiment-eligibility/index.tsx';
import { useTrialDetails } from '../../../services/check-experiment-eligibility/useTrialDetails.tsx';
import { AddPeopleGraphic } from './assets/addPeopleGraphic.tsx';
import { AttachmentsGraphic } from './assets/attachmentsGraphic.tsx';
import { AuditLogGraphic } from './assets/auditLogGraphic.tsx';
import { AutomationGraphic } from './assets/automationGraphic.tsx';
import { UserPermissionsGraphic } from './assets/userPermissionsGraphic.tsx';
import { BenefitsListSection } from './benefits-list-section/index.tsx';
import messages from './messages.tsx';

type SplitPanelModalProps = {
	userCanPay: boolean;
	upgradeUrl: string;
};

export const SplitPanelModal = ({ userCanPay, upgradeUrl }: SplitPanelModalProps) => (
	<JSErrorBoundary
		id="splitPanelModal"
		packageName="jiraLossAversion"
		teamName="luna-growth"
		fallback="unmount"
	>
		<SplitPanelModalContent userCanPay={userCanPay} upgradeUrl={upgradeUrl} />
	</JSErrorBoundary>
);

export const SplitPanelModalContent = ({ userCanPay, upgradeUrl }: SplitPanelModalProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { formatMessage } = useIntl();
	const [showModal, setShowModal] = useState(true);

	// since we're loading this modal, we must be eligible for the experiment
	// and therefore do not have payment on file
	const { isUrgent } = useExperimentEligibility(EXPERIMENT_NAME_AB, false);

	const { trialDaysLeft } = useTrialDetails();
	const origin = new OriginTracer({ product: 'jira' });
	const addPaymentDetailsUrl = origin.addToUrl(upgradeUrl);

	const onClose = useCallback(() => {
		fireButtonAnalytics(createAnalyticsEvent, 'jlaCloseButton', {
			trialDaysLeft,
			userCanPay,
			modal: 'jlaSplitPanel',
		});
		setShowModal(false);
	}, [createAnalyticsEvent, trialDaysLeft, userCanPay]);

	const onAddPaymentDetails = useCallback(() => {
		fireButtonAnalytics(createAnalyticsEvent, 'jlaAddPaymentButton', {
			trialDaysLeft,
			userCanPay,
			modal: 'jlaSplitPanel',
		});

		setShowModal(false);
	}, [createAnalyticsEvent, trialDaysLeft, userCanPay]);

	const getSubTitleText = useCallback(() => {
		if (isUrgent) {
			return userCanPay
				? formatMessage(messages.jswStandardSubTitleUrgent)
				: formatMessage(messages.jswStandardSubTitleUrgentNoPay);
		}
		return userCanPay
			? formatMessage(messages.jswStandardSubTitle)
			: formatMessage(messages.jswStandardSubTitleNoPay);
	}, [formatMessage, isUrgent, userCanPay]);

	return (
		<ContextualAnalyticsData sourceType={MODAL} sourceName="jiraLossAversionSplit">
			{showModal && (
				<Modal
					height={536}
					title={formatMessage(messages.jswStandardTitle, {
						trialDaysLeft,
					})}
					subtitle={getSubTitleText()}
					onClose={onClose}
					testId="loss-aversion.ui.loss-aversion-modal.split-panel"
					actions={
						userCanPay && addPaymentDetailsUrl ? (
							<>
								<Button onClick={onClose} appearance="subtle">
									{formatMessage(messages.jswStandardSecondaryCtaText)}
								</Button>

								<LinkButton
									onClick={onAddPaymentDetails}
									appearance="primary"
									href={addPaymentDetailsUrl}
									target="_blank"
								>
									{formatMessage(messages.jswStandardPrimaryCtaText)}
								</LinkButton>
							</>
						) : (
							<Button onClick={onClose} appearance="subtle">
								{formatMessage(messages.jswStandardPrimaryCtaTextNoPay)}
							</Button>
						)
					}
					right={
						userCanPay ? (
							<Box xcss={carouselStyles}>
								<Carousel height={325}>
									<CarouselSlide title={formatMessage(messages.jswStandardCarouslSlideUserLimit)}>
										<AddPeopleGraphic />
									</CarouselSlide>
									<CarouselSlide title={formatMessage(messages.jswStandardCarouslSlideAuditLog)}>
										<AuditLogGraphic />
									</CarouselSlide>
									<CarouselSlide
										title={formatMessage(messages.jswStandardCarouslSlideUserAutomation)}
									>
										<AutomationGraphic />
									</CarouselSlide>
									<CarouselSlide
										title={formatMessage(messages.jswStandardCarouslSlideUserPermissions)}
									>
										<UserPermissionsGraphic />
									</CarouselSlide>
								</Carousel>
							</Box>
						) : (
							<Box xcss={carouselStyles}>
								<Carousel height={325}>
									<CarouselSlide title={formatMessage(messages.jswStandardCarouslSlideUserLimit)}>
										<AddPeopleGraphic />
									</CarouselSlide>
									<CarouselSlide
										title={formatMessage(messages.jswStandardCarouslSlideStorageLimit)}
									>
										<AttachmentsGraphic />
									</CarouselSlide>
									<CarouselSlide
										title={formatMessage(messages.jswStandardCarouslSlideUserAutomation)}
									>
										<AutomationGraphic />
									</CarouselSlide>
								</Carousel>
							</Box>
						)
					}
				>
					<BenefitsListSection canPay={userCanPay} />
				</Modal>
			)}
			<FireScreenAnalytics
				attributes={{ experiment: EXPERIMENT_NAME_AB, trialDaysLeft, userCanPay }}
			/>
		</ContextualAnalyticsData>
	);
};

const carouselStyles = xcss({
	margin: 'auto',
});
